import {
  accommodationTypes, evidenceLabel, genderFocusLabel, outreachStages, partnerStatuses, referralForm, supportGroups, teamStatus, unitTypes,
} from './adminData.js';

// The dropdowns above the housing providers list. Every provider is loaded, so they filter in the browser and each choice says
// how many providers it would show. A filter gives each provider the values it matches (`keys`); choosing a value keeps the
// providers that have it. The first group is who the provider is, the second where it stands with us.

const list = (value) => (Array.isArray(value) ? value : []);
const day = 24 * 60 * 60 * 1000;

const nations = ['England', 'Scotland', 'Wales', 'Northern Ireland'];
const referralForms = ['online_form', 'download', 'page_only', 'none'];
const teamStatuses = ['team', 'one_person', 'no_names', 'not_found', 'site_down', 'no_website'];

/** The councils a provider covers as `council:<code>`, or its broad areas as `region:<name>` when it names no single council. */
function areaKeys(row) {
  const councils = list(row.councils);
  return councils.length > 0 ? councils.map((council) => `council:${council.code}`) : list(row.regions).map((region) => `region:${region}`);
}

/** Every council and broad area among `rows`, by name. */
function areaChoices(rows) {
  const names = new Map();
  for (const row of rows) {
    const councils = list(row.councils);
    if (councils.length > 0) for (const council of councils) names.set(`council:${council.code}`, council.name || council.code);
    else for (const region of list(row.regions)) names.set(`region:${region}`, region);
  }
  return [...names].sort((a, b) => a[1].localeCompare(b[1], 'en-GB'));
}

function ageKeys(row) {
  const low = Number.isFinite(row.age_min) ? row.age_min : null;
  const high = Number.isFinite(row.age_max) ? row.age_max : null;
  if (low === null && high === null) return ['none'];
  return [
    ...(low !== null && low < 18 ? ['under_18'] : []),
    ...(high !== null && high <= 25 ? ['up_to_25'] : []),
    ...(low !== null && low >= 18 ? ['adults'] : []),
  ];
}

/** Within the last week, the last 30 days, or before that, from when the provider was added to the list. */
function addedKeys(row, now) {
  const added = Date.parse(row.added_on ?? '');
  if (Number.isNaN(added)) return [];
  const age = now - added;
  if (age <= 7 * day) return ['week', 'month'];
  return age <= 30 * day ? ['month'] : ['older'];
}

/** Who the provider is. */
export const identityFilters = [
  { key: 'area', label: 'Area', any: 'Any area', choices: areaChoices, keys: areaKeys },
  {
    key: 'nation', label: 'Nation', any: 'Any nation', choices: [...nations.map((nation) => [nation, nation]), ['none', 'Nation not stated']],
    keys: (row) => (list(row.nations).length > 0 ? list(row.nations) : ['none']),
  },
  {
    key: 'support', label: 'Who they support', any: 'Anyone they support', choices: [...supportGroups, ['none', 'Nobody found']],
    keys: (row) => (list(row.support_groups).length > 0 ? list(row.support_groups) : ['none']),
  },
  {
    key: 'gender', label: 'Men or women', any: 'Men or women',
    choices: [...['women_only', 'men_only', 'mixed'].map((value) => [value, genderFocusLabel(value)]), ['none', 'Not stated']],
    keys: (row) => [row.gender_focus || 'none'],
  },
  {
    key: 'age', label: 'Age', any: 'Any age',
    choices: [['under_18', 'Takes under-18s'], ['up_to_25', 'Up to 25 only'], ['adults', 'Adults only (18+)'], ['none', 'No age limit stated']],
    keys: ageKeys,
  },
  {
    key: 'accommodation', label: 'Accommodation', any: 'Any accommodation', choices: [...accommodationTypes, ['none', 'Nothing found']],
    keys: (row) => (list(row.accommodation_types).length > 0 ? list(row.accommodation_types) : ['none']),
  },
  {
    key: 'rooms', label: 'Rooms', any: 'Any rooms', choices: [...unitTypes, ['none', 'Rooms not stated']],
    keys: (row) => (list(row.unit_types).length > 0 ? list(row.unit_types) : ['none']),
  },
];

/** Where the provider stands with us. */
export const standingFilters = [
  {
    key: 'stage', label: 'Outreach', any: 'Any outreach stage', choices: outreachStages.map(([value, words]) => [value, words]),
    keys: (row) => [row.outreach_stage || 'not_contacted'],
  },
  {
    key: 'reply', label: 'Reply', any: 'Any reply', choices: [...partnerStatuses.map(([value, words]) => [value, words]), ['none', 'No reply yet']],
    keys: (row) => [row.partner_status || 'none'],
  },
  {
    key: 'selfReferral', label: 'Self-referral', any: 'Self-referral or not', choices: [['yes', 'Takes self-referrals'], ['no', 'No self-referral found']],
    keys: (row) => [row.self_referral ? 'yes' : 'no'],
  },
  {
    key: 'referral', label: 'Referral form', any: 'Any referral form',
    choices: [...referralForms.map((value) => [value, referralForm(value).label]), ['unchecked', 'Form not checked']],
    keys: (row) => [row.referral_form || 'unchecked'],
  },
  {
    key: 'team', label: 'Team page', any: 'Any team page',
    choices: [...teamStatuses.map((value) => [value, teamStatus(value).label]), ['unchecked', 'Website not checked']],
    keys: (row) => [row.team_status || 'unchecked'],
  },
  {
    key: 'priority', label: 'Priority', any: 'Any priority', choices: [['1', 'Priority 1'], ['2', 'Priority 2'], ['3', 'Priority 3'], ['none', 'No priority']],
    keys: (row) => [row.priority ? String(row.priority) : 'none'],
  },
  {
    key: 'evidence', label: 'Evidence', any: 'Any evidence',
    choices: [...['explicit_hmo', 'shared_house', 'awaiting'].map((value) => [value, evidenceLabel(value)]), ['none', 'No evidence noted']],
    keys: (row) => [row.evidence_level || 'none'],
  },
  {
    key: 'added', label: 'Added', any: 'Added any time', choices: [['week', 'Added in the last 7 days'], ['month', 'Added in the last 30 days'], ['older', 'Added over 30 days ago']],
    keys: addedKeys,
  },
];

export const providerFilters = [...identityFilters, ...standingFilters];

/** Whether the search box's words appear in the provider's name, email, website, towns, broad areas or councils. */
export function matchesSearch(row, query) {
  const words = String(query ?? '').trim().toLowerCase();
  if (!words) return true;
  const places = [...list(row.service_areas), ...list(row.regions), ...list(row.councils).map((council) => council.name || council.code)];
  return [row.organisation, row.email, row.website, ...places].some((text) => String(text ?? '').toLowerCase().includes(words));
}

const passes = (filter, row, value, now) => !value || filter.keys(row, now).includes(value);

/** The providers that match the search and every chosen value (`chosen` is `{ filterKey: value }`; '' or missing is any). */
export function filterProviders(rows, chosen, query, now = Date.now()) {
  return rows.filter((row) => matchesSearch(row, query) && providerFilters.every((filter) => passes(filter, row, chosen[filter.key], now)));
}

/**
 * The choices each dropdown offers: `{ filterKey: [[value, words, number], …] }`, where `number` is how many providers that
 * choice would show with the search and the other filters as they are. Choices that would show nobody are left out, except
 * the one chosen.
 */
export function filterChoices(rows, chosen, query, now = Date.now()) {
  const searched = rows.filter((row) => matchesSearch(row, query));
  return Object.fromEntries(providerFilters.map((filter) => {
    const others = searched.filter((row) => providerFilters.every((other) => other === filter || passes(other, row, chosen[other.key], now)));
    const counts = new Map();
    for (const row of others) for (const key of new Set(filter.keys(row, now))) counts.set(key, (counts.get(key) || 0) + 1);
    const choices = typeof filter.choices === 'function' ? filter.choices(rows) : filter.choices;
    return [filter.key, choices
      .map(([value, words]) => [value, words, counts.get(value) || 0])
      .filter(([value, , number]) => number > 0 || value === chosen[filter.key])];
  }));
}
